import { createAction } from '@reduxjs/toolkit';
import Axios, { type CancelTokenSource } from 'axios';

import type {
  EndpointId,
  EndpointRequest,
} from '@iconik-io/iconik-api/src/client/request';
import { EndpointError } from '@iconik-io/iconik-api/src/client/response';

export const httpRequestNetworkError = createAction<EndpointError>(
  'HTTP_REQUEST_NETWORK_ERROR',
);

export const httpCancelRequests = createAction<EndpointId[]>(
  'HTTP_CANCEL_REQUESTS',
);

export const cancellable = <Request extends EndpointRequest>(
  request: Request,
  cancelKey?: string,
): Request => ({
  ...request,
  meta: {
    ...request.meta,
    cancellable: true,
    cancelKey,
  },
});

export const skipRedirectOnLogin = <Request extends EndpointRequest>(
  request: Request,
): Request => ({
  ...request,
  meta: {
    ...request.meta,
    skipLoginRedirect: true,
  },
});

/**
 * A 401 normally clears the stored token, on the assumption that it was the
 * token being rejected. Requests that authenticate with credentials of their own
 * say so here, so a rejection of those does not sign the viewer out of the app.
 */
export const keepCredentialsOnUnauthorized = <Request extends EndpointRequest>(
  request: Request,
): Request => ({
  ...request,
  meta: {
    ...request.meta,
    keepCredentialsOnUnauthorized: true,
  },
});

export const skipNormalizer = <Request extends EndpointRequest>(
  request: Request,
): Request => ({
  ...request,
  meta: {
    ...request.meta,
    skipNormalizer: true,
  },
});

export const skipTokenRefresh = <Request extends EndpointRequest>(
  request: Request,
): Request => ({
  ...request,
  meta: {
    ...request.meta,
    canRefresh: false,
  },
});

export const injectCancelSource = <Request extends EndpointRequest>(
  request: Request,
  cancel: CancelTokenSource,
): Request => ({
  ...request,
  meta: {
    ...request.meta,
    cancel,
  },
});

export const isCancellation = (error: unknown): boolean =>
  error instanceof EndpointError && Axios.isCancel(error.cause);

export const ignoreShareAuth = <Request extends EndpointRequest>(
  request: Request,
): Request => ({
  ...request,
  meta: {
    ...request.meta,
    ignoreShareAuth: true,
  },
});
